/**
 * computeTotals.ts — единая функция расчёта итогов сметы (PDF-03 / CLAUDE.md rule 8).
 *
 * ПРАВИЛО ОКРУГЛЕНИЯ (польская конвенция kosztorys):
 *   - Каждая строка округляется: roundTo(finalQty * priceLabor, 2)
 *   - netTotal = сумма уже округлённых строк (НЕ округление суммы)
 *   - vatAmount = roundTo(netTotal * vatRate, 2)
 *   - grossTotal = roundTo(netTotal + vatAmount, 2)
 *
 * ЗАПРЕЩЕНО: toFixed / Math.round прямо — только roundTo из shared/lib/number.
 * ЗАПРЕЩЕНО: хардкод ставок НДС (0.08 / 0.23) — vatRate передаётся параметром.
 *
 * Эта функция — ЕДИНСТВЕННЫЙ источник истины для итогов.
 * Все три пути (calculator, Excel, PDF) должны вызывать её.
 */
import { resolveEffectiveRoomGeometry, resolveEffectiveProjectGeometry, resolveUnitQuantity } from '@/shared/lib/geometry';
import { parseSafeNumber, roundTo } from '@/shared/lib/number';
import { PERCENT_DIVISOR } from '@/shared/lib/geometry-constants';
import type { Room } from '@/entities/room/model/types';
import type { RoomGeometry } from '@/shared/model';
import type { EstimateItem, TypedAdjustment } from '../model/types';

// ---------------------------------------------------------------------------
// Result types (exported — consumed by PDF, Excel, and calculator paths)
// ---------------------------------------------------------------------------

export interface LineResult {
  /** Identifies the source EstimateItem */
  itemId: string;
  /** Human-readable service name (WR-01/WR-02: avoids UUID in signed PDF columns). */
  name: string;
  /** Final computed quantity after geometry resolution, user override, adjustment, and margin */
  finalQty: number;
  /** Labor price from the EstimateItem */
  priceLabor: number;
  /** roundTo(finalQty * priceLabor, 2) — already rounded, NEVER re-round in consumers */
  lineTotal: number;
}

export interface EstimateTotalsResult {
  /** One LineResult per EstimateItem across all rooms */
  lines: LineResult[];
  /** Sum of all already-rounded lineTotals (PL kosztorys: round-line-then-sum) */
  netTotal: number;
  /** roundTo(netTotal * vatRate, 2) */
  vatAmount: number;
  /** roundTo(netTotal + vatAmount, 2) */
  grossTotal: number;
}

// ---------------------------------------------------------------------------
// Core computation
// ---------------------------------------------------------------------------

/**
 * Computes line-level and aggregate totals for a set of rooms.
 *
 * @param rooms          - Rooms with their items
 * @param projectItems   - Project-level (global) items (not room-scoped)
 * @param safetyMargin   - Safety/waste margin in percent (e.g. 10 = 10%)
 * @param vatRate        - VAT rate as a decimal (e.g. 0.23 for 23%; from vat-constants.ts)
 * @param projectOverrides - Optional manual overrides for project-level geometry
 */
export function computeEstimateTotals(
  rooms: Room[],
  projectItems: EstimateItem[],
  safetyMargin: number,
  vatRate: number,
  projectOverrides?: { floorArea?: number; wallArea?: number; perimeter?: number },
): EstimateTotalsResult {
  const marginFactor = 1 + (safetyMargin / PERCENT_DIVISOR);
  const lines: LineResult[] = [];
  let netTotal = 0;

  // ---------------------------------------------------------------------------
  // Pass 1: Room items
  // ---------------------------------------------------------------------------
  for (const room of rooms) {
    const roomGeom = resolveEffectiveRoomGeometry(room);

    for (const item of room.items || []) {
      const line = computeItemLine(item, roomGeom, marginFactor);
      lines.push(line);
      netTotal += line.lineTotal;
    }
  }

  // ---------------------------------------------------------------------------
  // Pass 2: Project-level (global) items
  // ---------------------------------------------------------------------------
  if (projectItems && projectItems.length > 0) {
    const projectGeom = resolveEffectiveProjectGeometry(rooms, projectOverrides);

    for (const item of projectItems) {
      const userQty = parseSafeNumber(item.userQty, 0);
      const baseQty = resolveUnitQuantity(item.unitType || 'pcs', projectGeom, userQty);
      const hasGlobalGeom =
        projectGeom.floorArea > 0 ||
        projectGeom.wallArea > 0 ||
        projectGeom.perimeter > 0;
      let workingQty = userQty > 0 ? userQty : (hasGlobalGeom ? baseQty : 0);

      workingQty = applyTypedAdjustment(workingQty, item.adjustment);

      const finalQty = Math.max(0, workingQty * marginFactor);
      const price = parseSafeNumber(item.priceLabor, 0);
      const lineTotal = roundTo(finalQty * price, 2);

      lines.push({
        itemId: item.id,
        name: item.name ?? '',
        finalQty,
        priceLabor: price,
        lineTotal,
      });
      netTotal += lineTotal;
    }
  }

  // netTotal is the sum of already-rounded line totals — no further rounding of the sum
  const vatAmount = roundTo(netTotal * vatRate, 2);
  const grossTotal = roundTo(netTotal + vatAmount, 2);

  return { lines, netTotal, vatAmount, grossTotal };
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Computes a single LineResult for a room item.
 */
function computeItemLine(
  item: EstimateItem,
  roomGeom: RoomGeometry,
  marginFactor: number,
): LineResult {
  const unitType = item.unitType || 'pcs';
  const userQty = parseSafeNumber(item.userQty, 0);
  const baseQty = resolveUnitQuantity(unitType, roomGeom, userQty);
  let workingQty = userQty > 0 ? userQty : baseQty;

  workingQty = applyTypedAdjustment(workingQty, item.adjustment);

  const finalQty = Math.max(0, workingQty * marginFactor);
  const price = parseSafeNumber(item.priceLabor, 0);
  const lineTotal = roundTo(finalQty * price, 2);

  return {
    itemId: item.id,
    name: item.name ?? '',
    finalQty,
    priceLabor: price,
    lineTotal,
  };
}

/**
 * Applies a typed adjustment to a working quantity.
 * No regex, no String() parsing — consumes the typed object directly.
 *
 * op 'add' → workingQty += value
 * op 'sub' → workingQty -= value
 * op 'mul' → workingQty *= value (default; { op:'mul', value:1 } is a no-op)
 */
export function applyTypedAdjustment(workingQty: number, adjustment: TypedAdjustment | undefined): number {
  if (!adjustment) return workingQty;
  if (adjustment.op === 'add') return workingQty + adjustment.value;
  if (adjustment.op === 'sub') return workingQty - adjustment.value;
  return workingQty * adjustment.value; // 'mul'
}
